// @alamo/ui · ThemePicker
//
// Radio-card theme selector with per-mode layout thumbnails (Jira "Theme"
// pattern): Light / Dark / Auto ("match system"). Presentation-only — it
// receives the current `mode` and reports selection via `onSelect`. The
// consumer owns applying (`useTheme().setMode`) and persistence
// (`useUpdateUserPreferences`).

import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ThemeMode } from '@alamo/types';
import { THEME_MODES } from '@alamo/types';

import { cn } from '../../lib/cn';

import { ThemeThumbnail } from './ThemeThumbnail';

export interface ThemePickerProps {
  /** Currently selected preference (from `useTheme().mode`). */
  mode: ThemeMode;
  /** Fired when a card is chosen. */
  onSelect: (mode: ThemeMode) => void;
  /**
   * `horizontal` (default) lays the 3 cards side by side (profile/desktop);
   * `vertical` stacks them full-width (compact dropdown/mobile).
   */
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  /** Accessible label for the radiogroup (i18n by consumer if overriding). */
  ariaLabel?: string;
}

/**
 * ThemePicker — 3 radio cards, each a mini layout mock + label + selected ring.
 *
 * Selection ring = azul-protecao border + sel-tint-laranja bg (consistent with
 * RadioCard). Active card also shows a laranja check badge. Thumbnails are
 * pure inline SVG (no external image). Tap targets ≥44px (mobile-first).
 */
export function ThemePicker({
  mode,
  onSelect,
  orientation = 'horizontal',
  className,
  ariaLabel,
}: ThemePickerProps) {
  const { t } = useTranslation('ui');

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel ?? t('theme_picker.aria_label')}
      className={cn(
        'gap-2',
        orientation === 'horizontal' ? 'grid grid-cols-3' : 'flex flex-col',
        className,
      )}
    >
      {THEME_MODES.map((value) => {
        const selected = value === mode;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={t(`theme_picker.aria.${value}`)}
            onClick={() => onSelect(value)}
            className={cn(
              'group relative flex rounded-xl border-2 p-2 text-left transition-all',
              'min-h-[44px] focus:outline-none focus:ring-2 focus:ring-azul-protecao focus:ring-offset-1',
              orientation === 'horizontal' ? 'flex-col gap-2' : 'flex-row items-center gap-3',
              selected
                ? 'border-azul-protecao bg-sel-tint-laranja'
                : 'border-slate-200 bg-white hover:border-slate-300',
            )}
          >
            <ThemeThumbnail
              mode={value}
              className={cn(
                'rounded-lg',
                orientation === 'horizontal' ? 'h-auto w-full' : 'h-12 w-20 shrink-0',
              )}
            />

            <span
              className={cn(
                'flex items-center gap-1.5 font-body text-xs font-semibold',
                orientation === 'horizontal' ? 'justify-center' : 'flex-1',
                selected ? 'text-azul-protecao' : 'text-ink',
              )}
            >
              {t(`theme_picker.label.${value}`)}
            </span>

            {selected && (
              <span
                aria-hidden
                className={cn(
                  'flex size-4 items-center justify-center rounded-full bg-laranja-encanto text-white',
                  orientation === 'horizontal' ? 'absolute right-1.5 top-1.5' : 'shrink-0',
                )}
              >
                <Check className="size-3" strokeWidth={3} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
